import React, { useState, useEffect } from "react";
import Layout from "../component/Layout";
import "./Homepage.css";
import { db } from "../services/firebase";
import { collection, getDocs, doc, getDoc, setDoc } from "firebase/firestore";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#8a6b32", "#d4af37", "#c0c0c0", "#e5e4e2", "#cd7f32"];

const Homepage = () => {
  const [stats, setStats] = useState({
    revenue: 0,
    orders: 0,
    buyers: 0,
    sellers: 0,
    dailyOnboardedBuyers: 0,
    monthlyOnboardedBuyers: 0,
    dailyOnboardedSellers: 0,
    monthlyOnboardedSellers: 0,
  });

  const [targets, setTargets] = useState({
    sellerTarget: "",
    buyerAreaTarget: "",
    operatorPlan: "",
  });
  
  const [targetHistory, setTargetHistory] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempTargets, setTempTargets] = useState({ ...targets });

  const [monthlySalesData, setMonthlySalesData] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const [metalMilestones, setMetalMilestones] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    fetchTargets();
  }, []);

  const fetchTargets = async () => {
    try {
      const docRef = doc(db, "settings", "dashboardTargets");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setTargets({
          sellerTarget: data.sellerTarget || "",
          buyerAreaTarget: data.buyerAreaTarget || "",
          operatorPlan: data.operatorPlan || "",
        });
        setTargetHistory(data.history || []);
      }
    } catch (error) {
      console.error("Error fetching targets", error);
    }
  };

  const openModal = () => {
    setTempTargets({ ...targets });
    setIsModalOpen(true);
  };

  const saveTargets = async () => {
    try {
      const docRef = doc(db, "settings", "dashboardTargets");
      const docSnap = await getDoc(docRef);
      
      let existingHistory = [];
      let oldTargets = { sellerTarget: "", buyerAreaTarget: "", operatorPlan: "" };
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        existingHistory = data.history || [];
        oldTargets = {
          sellerTarget: data.sellerTarget || "",
          buyerAreaTarget: data.buyerAreaTarget || "",
          operatorPlan: data.operatorPlan || ""
        };
      }

      const newHistoryEntry = {
        updatedAt: new Date().toISOString(),
        oldValues: oldTargets,
        newValues: tempTargets
      };

      const newHistory = [newHistoryEntry, ...existingHistory].slice(0, 50);

      await setDoc(docRef, {
        ...tempTargets,
        history: newHistory
      }, { merge: true });
      
      setTargets({ ...tempTargets });
      setTargetHistory(newHistory);
      setIsModalOpen(false);
      alert("Targets saved successfully!");
    } catch (error) {
      console.error("Error saving targets", error);
      alert("Failed to save targets");
    }
  };

  const handleTargetChange = (e) => {
    setTempTargets({ ...tempTargets, [e.target.name]: e.target.value });
  };

  const fetchDashboardData = async () => {
    try {
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

      // Fetch Buyers
      const buyersSnap = await getDocs(collection(db, "buyers"));
      let dailyBuyers = 0;
      let monthlyBuyers = 0;
      buyersSnap.forEach((doc) => {
        const data = doc.data();
        let createdAt = data.createdAt;
        if (createdAt && createdAt.toDate) createdAt = createdAt.toDate().getTime();
        else if (createdAt) createdAt = new Date(createdAt).getTime();

        if (createdAt >= startOfDay) dailyBuyers++;
        if (createdAt >= startOfMonth) monthlyBuyers++;
      });

      // Fetch Sellers
      const sellersSnap = await getDocs(collection(db, "sellers"));
      let dailySellers = 0;
      let monthlySellers = 0;
      sellersSnap.forEach((doc) => {
        const data = doc.data();
        let createdAt = data.createdAt;
        if (createdAt && createdAt.toDate) createdAt = createdAt.toDate().getTime();
        else if (createdAt) createdAt = new Date(createdAt).getTime();

        if (createdAt >= startOfDay) dailySellers++;
        if (createdAt >= startOfMonth) monthlySellers++;
      });

      // Fetch Orders
      const ordersSnap = await getDocs(collection(db, "orderList"));
      let totalRevenue = 0;
      let orderCount = ordersSnap.size;
      let metalBreakdown = {};
      let monthlyRevenue = {};

      ordersSnap.forEach((doc) => {
        const data = doc.data();
        const amount = Number(data.totalAmount || data.price || data.amount || 0);
        totalRevenue += amount;

        // Breakdown by metal
        const metal = data.metal || data.category || "Other";
        metalBreakdown[metal] = (metalBreakdown[metal] || 0) + 1;

        // Group by month
        let dateObj = data.createdAt;
        if (dateObj && dateObj.toDate) dateObj = dateObj.toDate();
        else if (dateObj) dateObj = new Date(dateObj);
        else dateObj = new Date();

        const month = dateObj.toLocaleString('default', { month: 'short' });
        if (!monthlyRevenue[month]) {
          monthlyRevenue[month] = { name: month, sales: 0, orders: 0 };
        }
        monthlyRevenue[month].sales += amount;
        monthlyRevenue[month].orders += 1;
      });

      const formattedMonthlySales = Object.values(monthlyRevenue);
      const formattedMetalMilestones = Object.keys(metalBreakdown).map(key => ({
        name: key,
        value: metalBreakdown[key]
      }));

      setStats({
        revenue: totalRevenue,
        orders: orderCount,
        buyers: buyersSnap.size,
        sellers: sellersSnap.size,
        dailyOnboardedBuyers: dailyBuyers,
        monthlyOnboardedBuyers: monthlyBuyers,
        dailyOnboardedSellers: dailySellers,
        monthlyOnboardedSellers: monthlySellers,
      });

      setMonthlySalesData(formattedMonthlySales.length > 0 ? formattedMonthlySales : [
        { name: "Jan", sales: 0, orders: 0 }
      ]);

      setCustomerData([
        { name: "Sellers", value: sellersSnap.size },
        { name: "Buyers", value: buyersSnap.size },
      ]);

      setMetalMilestones(formattedMetalMilestones);

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  return (
    <Layout pageTitle="Super Admin Dashboard">
      {/* Top Stat Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h4>Revenue</h4>
          <h2>₹{stats.revenue.toLocaleString()}</h2>
          <p>Total platform revenue</p>
        </div>
        <div className="stat-card">
          <h4>Orders</h4>
          <h2>{stats.orders}</h2>
          <p>Total orders placed</p>
        </div>
        <div className="stat-card">
          <h4>Buyers</h4>
          <h2>{stats.buyers}</h2>
          <p>Today: {stats.dailyOnboardedBuyers} | This Month: {stats.monthlyOnboardedBuyers}</p>
        </div>
        <div className="stat-card">
          <h4>Sellers</h4>
          <h2>{stats.sellers}</h2>
          <p>Today: {stats.dailyOnboardedSellers} | This Month: {stats.monthlyOnboardedSellers}</p>
        </div>
      </div>

      {/* Targets Section View */}
      <div className="dashboard-targets-section">
        <div className="targets-header">
          <h3>Dashboard Configuration / Targets</h3>
          <button className="edit-btn" onClick={openModal}>Edit Targets & View History</button>
        </div>
        <div className="targets-grid readonly">
          <div className="target-display">
            <span className="target-label">Monthly Target of Seller:</span>
            <span className="target-value">{targets.sellerTarget || "N/A"}</span>
          </div>
          <div className="target-display">
            <span className="target-label">Monthly Target of Buyer Area:</span>
            <span className="target-value">{targets.buyerAreaTarget || "N/A"}</span>
          </div>
          <div className="target-display">
            <span className="target-label">Operator Plan:</span>
            <span className="target-value">{targets.operatorPlan || "N/A"}</span>
          </div>
        </div>
      </div>

      {/* Target Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Edit Targets</h2>
            <div className="modal-body">
              <div className="target-input-group">
                <div className="target-input">
                  <label>Monthly Target of Seller</label>
                  <input 
                    type="number" 
                    name="sellerTarget"
                    value={tempTargets.sellerTarget} 
                    onChange={handleTargetChange}
                    placeholder="e.g. 100"
                  />
                </div>
                <div className="target-input">
                  <label>Monthly Target of Buyer Area</label>
                  <input 
                    type="number" 
                    name="buyerAreaTarget"
                    value={tempTargets.buyerAreaTarget} 
                    onChange={handleTargetChange}
                    placeholder="e.g. 50"
                  />
                </div>
                <div className="target-input">
                  <label>Operator Plan</label>
                  <input 
                    type="text" 
                    name="operatorPlan"
                    value={tempTargets.operatorPlan} 
                    onChange={handleTargetChange}
                    placeholder="e.g. Gold Tier"
                  />
                </div>
              </div>

              <div className="target-history-section">
                <h3>Entry and Edit History</h3>
                <div className="history-list">
                  {targetHistory.length > 0 ? targetHistory.map((item, idx) => (
                    <div key={idx} className="history-item">
                      <div className="history-time">{new Date(item.updatedAt).toLocaleString()}</div>
                      <div className="history-changes">
                        <div className="history-change">
                          Seller Target: {item.oldValues.sellerTarget || "0"} ➔ {item.newValues.sellerTarget || "0"}
                        </div>
                        <div className="history-change">
                          Buyer Area Target: {item.oldValues.buyerAreaTarget || "0"} ➔ {item.newValues.buyerAreaTarget || "0"}
                        </div>
                        <div className="history-change">
                          Operator Plan: {item.oldValues.operatorPlan || "None"} ➔ {item.newValues.operatorPlan || "None"}
                        </div>
                      </div>
                    </div>
                  )) : (
                    <p className="no-history">No edit history found.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="save-btn" onClick={saveTargets}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className="charts-section">
        {/* Line Chart */}
        <div className="chart-container">
          <h3>Monthly Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlySalesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="sales" stroke="#8a6b32" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Bar Chart */}
        <div className="chart-container">
          <h3>Monthly Orders</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlySalesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="orders" fill="#d4af37" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Pie + Milestones */}
      <div className="bottom-charts">
        {/* Pie Chart */}
        <div className="chart-container">
          <h3>Users Breakdown</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={customerData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={100}
                dataKey="value"
              >
                {customerData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Milestones / Segments Chart */}
        <div className="chart-container">
          <h3>Metal-wise Order Volume (Milestones)</h3>
          {metalMilestones.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={metalMilestones}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {metalMilestones.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No metal segment data available</div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Homepage;